import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileUploadStatus, type UploadedFile } from './useUploadedFiles';

export function useSelectedFile(uploadedFiles: Map<string, UploadedFile>) {
  const [selectedFileRequestId, setSelectedFileRequestId] = useState<
    string | null
  >(null);

  const isFileSelectable = useCallback(
    (file: UploadedFile) => file.status === FileUploadStatus.Ready,
    [],
  );

  useEffect(() => {
    const currentSelection = selectedFileRequestId
      ? uploadedFiles.get(selectedFileRequestId)
      : undefined;
    const isCurrentSelectionValid =
      currentSelection && isFileSelectable(currentSelection);

    if (isCurrentSelectionValid) {
      return;
    }

    const firstSelectableFile = Array.from(uploadedFiles.entries())
      .filter(([, file]) => isFileSelectable(file))
      .sort(
        ([, a], [, b]) =>
          (a.orderNumber ?? Infinity) - (b.orderNumber ?? Infinity),
      )[0];
    setSelectedFileRequestId(firstSelectableFile?.[0] ?? null);
  }, [uploadedFiles, selectedFileRequestId, isFileSelectable]);

  const selectedFile = useMemo((): UploadedFile | undefined => {
    if (!selectedFileRequestId) return undefined;
    const file = uploadedFiles.get(selectedFileRequestId);
    if (!file || !isFileSelectable(file)) return undefined;
    return file;
  }, [uploadedFiles, selectedFileRequestId, isFileSelectable]);

  return selectedFile;
}
